import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Button, Drawer, IconButton, TextField, Typography } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { colors, drawerDividerColor, drawerDividerWidth, getDropdownFieldSx, getDropdownPaperSx,
  getErrorFieldSx, getDropdownOptionSx, errorTextSx, buttonContainedSx,
} from './globalFiltersStyles';
import FilterSection from './components/FilterSection';

const TENANT_REQUIRED_ERROR = 'Field cannot be empty.';

/* A deliberately minimal drawer used on Activation Engine routes. It exposes ONLY the 
  Tenant switch and no global-filter values. This way, it cannot leak or overwrite a 
  tenant's saved filters on apply, decoupling from the global filters.
 */
const TenantSwitchDrawer = ({ companies, currentTenantCd, filterOptions, onApply, onClose, open, showTenantFilter }) => {
  const currentTenant = Array.isArray(companies)
    ? companies.find((company) => company.code === currentTenantCd) || null
    : null;

  const [tenantValue, setTenantValue] = useState(currentTenant);
  const [hasTenantDraftSelection, setHasTenantDraftSelection] = useState(false);
  const [isTenantOpen, setIsTenantOpen] = useState(false);
  const [tenantError, setTenantError] = useState(null);
  const wasOpenRef = useRef(false);

  const effectiveTenantValue = hasTenantDraftSelection ? tenantValue : currentTenant;

  useEffect(() => {
    const isOpening = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!isOpening) {
      return;
    }

    setTenantValue(currentTenant);
    setHasTenantDraftSelection(false);
    setTenantError(null);
    setIsTenantOpen(false);
  }, [open, currentTenant]);

  const handleClose = (event, reason) => {
    onClose?.(event, reason);
  };

  const handleTenantChange = (event, value) => {
    setHasTenantDraftSelection(true);
    setTenantValue(value);
    setTenantError(null);
  };

  const handleApply = () => {
    if (showTenantFilter && !effectiveTenantValue) {
      setTenantError(TENANT_REQUIRED_ERROR);
      return;
    }

    onApply?.(effectiveTenantValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      BackdropProps={{
        sx: {
          backgroundColor: colors.backdrop
        }
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 672 },
          maxWidth: '100%',
          height: '100%',
          borderRadius: 0,
          backgroundColor: '#FFFFFF'
        }
      }}>
      <Box display="flex" flexDirection="column" height="100%" bgcolor="#FFFFFF">
        <Box px={3} py={3} borderBottom={`${drawerDividerWidth} solid ${drawerDividerColor}`}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <IconButton aria-label="Close tenant switch" onClick={(event) => handleClose(event, 'close-icon')} sx={{ p: 0 }}>
              <CloseIcon sx={{ fontSize: 24, color: colors.text }} />
            </IconButton>
          </Box>
        </Box>

        <Box flex={1} overflow="auto" px={3} pt={3} pb={5}>
          <Box display="flex" flexDirection="column" gap={3.5}>
            {showTenantFilter ? (
              <FilterSection label="Tenant" helperText="Search by tenant name or ID." helperPlacement="above">
                <Box display="flex" flexDirection="column" gap={1} width="100%">
                  <Autocomplete
                    disableClearable
                    open={isTenantOpen}
                    options={companies || []}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                    onChange={handleTenantChange}
                    onClose={() => setIsTenantOpen(false)}
                    onOpen={() => setIsTenantOpen(true)}
                    value={effectiveTenantValue}
                    filterOptions={filterOptions}
                    popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.iconFill, fontSize: 24 }} />}
                    slotProps={{
                      paper: { sx: getDropdownPaperSx(isTenantOpen, colors.primary) },
                      popper: { className: 'bst-autocomplete-popper', disablePortal: true }
                    }}
                    renderOption={(renderProps, option, { selected }) => {
                      const { className, key, ...optionProps } = renderProps;

                      return (
                        <Box component="li" key={key ?? option?.id ?? option?.code ?? option?.name} {...optionProps} sx={getDropdownOptionSx(selected)}>
                          {option?.name || ''}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder=""
                        sx={tenantError
                          ? getErrorFieldSx(getDropdownFieldSx(isTenantOpen ? colors.primary : colors.border))
                          : getDropdownFieldSx(isTenantOpen ? colors.primary : colors.border)}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Tenant Name/ID'
                        }}
                      />
                    )}
                  />
                  {tenantError ? <Typography sx={errorTextSx}>{tenantError}</Typography> : null}
                </Box>
              </FilterSection>
            ) : null}
          </Box>
        </Box>

        <Box borderTop={`${drawerDividerWidth} solid ${drawerDividerColor}`} px={3} py={3} bgcolor="#FFFFFF">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} flexWrap="wrap">
            <Box display="flex" gap={2} ml="auto">
              <Button
                variant="contained"
                onClick={handleApply}
                sx={buttonContainedSx}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TenantSwitchDrawer;
